import type { CrowdexCollaboration, CrowdexUIMessage } from '@crowdex/agent-contracts';
import {
  PromptInput,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  type PromptInputMessage,
  usePromptInputAttachments,
} from '@crowdex/ui/ai/prompt-input';
import {
  InlineReferenceEditor,
  type InlineReferenceEditorHandle,
} from '@crowdex/ui/components/inline-reference-editor';
import {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CloudConversationStartup } from '@/components/cloud-conversation-startup';
import {
  type NewConversationNeedsYou,
  NewConversationStart,
} from '@/components/desktop-chat-empty-state';
import { ComposerContextChips } from '@/components/desktop-shell/composer-context-chips';
import { ComposerMenuSideProvider } from '@/components/desktop-shell/composer-menu-side';
import type { ComposerPlusMenuHandle } from '@/components/desktop-shell/composer-plus-menu';
import {
  ComposerToolbar,
  type ComposerToolbarProps,
} from '@/components/desktop-shell/composer-toolbar';
import { AtTranscriptEnd } from '@/components/desktop-shell/conversation-error-banners';
import { ExpandableComposer } from '@/components/desktop-shell/expandable-composer';
import {
  FloatingPillHint,
  FloatingTargetChip,
} from '@/components/desktop-shell/floating-composer-chips';
import { useFloatingConversation } from '@/components/desktop-shell/floating-conversation';
import { QueuedFollowUpList } from '@/components/desktop-shell/queued-follow-up-list';
import type { MentionTarget } from '@/components/desktop-side-panel';
import { useShellNavigation } from '@/components/sidebar/shell-navigation';
import { ToolApprovalPanel } from '@/components/tool-approval-panel';
import { useAgentAsk } from '@/features/apps/agent-ask';
import { ComposerDictation } from '@/features/dictation/composer-dictation';
import { blockComposerSubmit } from '@/features/plugins/block-composer-submit';
import { usePluginConversationComposer } from '@/features/plugins/conversation-composer-context';
import { PluginComposerActions } from '@/features/plugins/plugin-composer-actions';
import { useLiveCallContext } from '@/features/voice-call/live-call';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useMessagePrediction } from '@/lib/agent-runtime/message-prediction';
import type { CloudConversationStartupPhase } from '@/lib/conversations/cloud-conversation-startup';
import { PREVIEW_PLAN_USAGE } from '@/lib/desktop-preview-data';
import { carryShownTabTo, restorePanel } from '@/lib/desktop-shell/app-panels';
import { COMPOSER_COMMANDS } from '@/lib/desktop-shell/composer-commands';
import type { PickerItem } from '@/lib/desktop-shell/composer-picker';
import { replaceCommandQuery, replaceMentionQuery } from '@/lib/desktop-shell/composer-query';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { latestUsage } from '@/lib/desktop-shell/message-derivations';
import type { RepositoryFile } from '@/lib/desktop-shell/repository-files';
import {
  type QueuedFollowUp,
  type StateSetter,
  shownConversationState,
} from '@/lib/desktop-shell/store';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { useRepositoryFiles } from '@/lib/desktop-shell/use-repository-files';
import { formatElementSelection } from '@/lib/element-selection';
import type { AvailablePluginSkill } from '@/lib/native-agent/types';
import type { NativeAgent } from '@/lib/native-desktop';
import { useOnline } from '@/lib/online-status';
import { isCloudRepositoryWorkspace } from '@/lib/sidebar-threads';
import { ComposerMenuPanel } from './composer-menu-list';
import { ComposerPicker, type ComposerPickerHandle, type PickerOpenScope } from './composer-picker';

/** Offline, the queue waits at the end of the transcript, as the messages it will be (D53). */
function QueuedFollowUps({ offline, children }: { offline: boolean; children: ReactNode }) {
  if (!offline) return <>{children}</>;
  return (
    <AtTranscriptEnd slot="queued">
      <div className="[&>ol]:mb-0">{children}</div>
    </AtTranscriptEnd>
  );
}

function ComposerReferenceEditor(props: ComponentProps<typeof InlineReferenceEditor>) {
  const attachments = usePromptInputAttachments();
  return <InlineReferenceEditor {...props} onFilesPaste={attachments.add} />;
}

/**
 * The composer region below the transcript: cloud-startup banner, the dock of what
 * the agent waits on (an approval, a question), the follow-up queue, the `PromptInput`
 * (with its slash/mention suggestion overlay and toolbar), and the worktree/menu
 * row. When a message is being edited the composer is portaled into the inline
 * editor's container. All of the composer's own state (draft text, menu
 * selection, git/handoff fields, editing container, follow-up queue) is read
 * from the store; the large callback surface (submit/keydown, model/mode
 * changes, mention/skill insertion, steer/queue actions, handoff controls) and
 * the connection/derived values arrive as props from `DesktopShell`.
 */
export function ConversationComposer({
  agent,
  startScreen = false,
  conversationTitle,
  needsYou = null,
  busy,
  preview,
  previewConversation,
  cloudAgentSelected,
  agentReady,
  canSubmit,
  status,
  planFirst,
  changePlanFirst,
  availableSkills,
  messages,
  cloudConversationStartupPhase,
  cloudWorkspacePanelActive,
  onToolApproval,
  mentionTargets,
  commandQuery,
  mentionQuery,
  skillQuery,
  modelConfigOptions,
  composerRef,
  changeModelConfig,
  insertArtifact,
  insertMention,
  insertSkill,
  onSubmit,
  onKeyDown,
  selectAgent,
  cloudHandoffWorking,
  onStop,
  onDeleteFollowUp,
  onReorderFollowUp,
  onSteerFollowUp,
  onClearFollowUps,
  workspaceControls,
  onOpenEnvironment,
  showAgentPicker = true,
  conversationId,
  composerText,
  setComposerText,
  editingMessageId,
  editingMessageContainer,
}: {
  agent: NativeAgent | null;
  /**
   * A new conversation in the pane: "What should we work on?", the composer with its setup
   * chips (`workspaceControls`), and what waits on the user under it.
   */
  startScreen?: boolean;
  /** Floating, who the composer writes to (D60). */
  conversationTitle?: string | undefined;
  /** The start screen's Needs you cards (the primary pane only). */
  needsYou?: NewConversationNeedsYou | null;
  busy: boolean;
  preview: boolean;
  previewConversation: boolean;
  cloudAgentSelected: boolean;
  agentReady: boolean;
  canSubmit: boolean;
  status: ComposerToolbarProps['status'];
  /** One agent, or a group (whose conversation sends to its members). */
  collaboration: CrowdexCollaboration;
  planFirst: ComposerToolbarProps['planFirst'];
  changePlanFirst: ComposerToolbarProps['changePlanFirst'];
  availableSkills: readonly AvailablePluginSkill[];
  messages: readonly CrowdexUIMessage[];
  cloudConversationStartupPhase: CloudConversationStartupPhase | null;
  cloudWorkspacePanelActive: boolean;
  onToolApproval: ComponentProps<typeof ToolApprovalPanel>['onAnswer'];
  /** What @ references: conversations, artifacts, tabs, terminals, saved agents. */
  mentionTargets: readonly MentionTarget[];
  commandQuery: string | null;
  mentionQuery: string | null;
  skillQuery: string | null;
  modelConfigOptions: ComposerToolbarProps['modelConfigOptions'];
  composerRef: RefObject<InlineReferenceEditorHandle | null>;
  changeModelConfig: ComposerToolbarProps['changeModelConfig'];
  insertArtifact: ComposerToolbarProps['insertArtifact'];
  insertMention: ComposerToolbarProps['insertMention'];
  insertSkill: ComposerToolbarProps['insertSkill'];
  onSubmit: (message: PromptInputMessage) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  selectAgent: ComposerToolbarProps['selectAgent'];
  cloudHandoffWorking: boolean;
  onStop: () => void;
  onDeleteFollowUp: (id: string) => void;
  onReorderFollowUp: (draggedId: string, targetId: string) => void;
  onSteerFollowUp: (item: QueuedFollowUp) => void;
  onClearFollowUps: () => void;
  /**
   * Active-pane-only setup chips of a new conversation (project, repository, branch, agent),
   * in the composer on the start screen. Splits pass nothing.
   */
  workspaceControls?: ReactNode;
  /** Opens where the conversation runs (the + menu's Environment). */
  onOpenEnvironment?: () => void;
  /** Show the agent switcher (primary pane only; agent is a workspace-level choice). */
  showAgentPicker?: boolean;
  conversationId: string;
  composerText: string;
  setComposerText: StateSetter<string>;
  editingMessageId: string | null;
  editingMessageContainer: HTMLDivElement | null;
}) {
  const composerScope = `${conversationId}:${editingMessageId ?? ''}`;
  const pickerRef = useRef<ComposerPickerHandle>(null);
  const plusMenuRef = useRef<ComposerPlusMenuHandle>(null);
  const [statusContainer, setStatusContainer] = useState<HTMLDivElement | null>(null);
  const [blockedPlugins, setBlockedPlugins] = useState<ReadonlySet<string>>(new Set());
  const onPluginBlocked = useCallback((id: string, blocked: boolean) => {
    setBlockedPlugins((current) => {
      if (current.has(id) === blocked) return current;
      const next = new Set(current);
      if (blocked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const submissionBlocked = blockedPlugins.size > 0;
  // The preview's agent has no modes: its + menu shows the boards' Plan mode (D7) all the same.
  const [previewPlanFirst, setPreviewPlanFirst] = useState(false);
  const shownPlanFirst = planFirst ?? (previewConversation ? { enabled: previewPlanFirst } : null);
  const changeShownPlanFirst =
    planFirst === null && previewConversation ? setPreviewPlanFirst : changePlanFirst;
  const focusEditor = () => composerRef.current?.focus();
  const setElementSelections = useDesktopShellStore((state) => state.setElementSelections);
  const insertText = (text: string) => {
    setComposerText((current) => `${current}${current && !/\s$/.test(current) ? ' ' : ''}${text}`);
    focusEditor();
  };
  // A plugin's `ask` is sent as the user would; while the agent cannot take it yet (it is
  // starting), it waits in the composer for the user to send.
  useAgentAsk(conversationId, {
    send: (text) => {
      if (canSubmit || busy) onSubmit({ text, files: [] });
      else insertText(text);
    },
    // A draft waits for the user: the reference first, then what they type after it.
    draft: insertArtifact,
    insert: insertText,
    // Elements commented on in an app join the composer's selections, as the browser's do.
    attach: (selections, send, text = '') => {
      setElementSelections((current) => [
        ...current,
        ...selections.map((selection, index) => ({
          id: crypto.randomUUID(),
          context: formatElementSelection(selection, current.length + index + 1),
          ...(selection.screenshot === undefined ? {} : { dataUrl: selection.screenshot }),
          title: selection.title,
          kind: 'app' as const,
        })),
      ]);
      if (send && (canSubmit || busy)) onSubmit({ text, files: [] });
      else if (text !== '') insertText(text);
      else focusEditor();
    },
  });
  const online = useOnline();
  const liveCall = useLiveCallContext();
  const voiceCallActive = liveCall !== null && liveCall.call.status !== 'idle';
  const floating = useFloatingConversation();
  const shellStore = useDesktopShellStoreApi();
  const navigation = useShellNavigation();
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const groupComposer = usePluginConversationComposer();
  // Only the active pane's composer knows its repository (a split's other half passes no controls).
  const repositoryFiles = useRepositoryFiles(
    groupComposer === null && workspaceControls !== undefined ? mentionQuery : null,
    preview,
  );
  const participants =
    groupComposer === null
      ? undefined
      : [
          ...(groupComposer.allowEveryone === false
            ? []
            : [{ id: '__all', name: 'Everyone', agentId: '', avatar: undefined }]),
          ...groupComposer.participants,
        ];
  // The @ filter the picker opens on (Mention a file, ⌘⇧A), until @ is typed away.
  const [mentionFilter, setMentionFilter] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (mentionQuery === null) setMentionFilter(undefined);
  }, [mentionQuery]);
  // One picker: / (or a $skill alone) for the skills and commands, @ for the references.
  const pickerScope =
    mentionQuery !== null
      ? 'mention'
      : commandQuery !== null || skillQuery !== null
        ? 'skills'
        : null;
  const pickerQuery = mentionQuery ?? commandQuery ?? skillQuery ?? '';
  /** The / or @ and what follows it, gone: the picker closes. */
  const clearPickerQuery = () =>
    setComposerText((current) =>
      mentionQuery !== null
        ? replaceMentionQuery(current, '')
        : commandQuery !== null
          ? replaceCommandQuery(current, '')
          : current.replace(/^\$[^\s]*$/, ''),
    );
  /** + → Mention a file, All skills, ⌘⇧A: the same picker, scoped by / or @ in the composer. */
  const openPickerScope = (scope: PickerOpenScope) => {
    setMentionFilter(scope === 'files' ? 'file' : scope === 'artifacts' ? 'artifact' : undefined);
    const trigger = scope === 'skills' ? '/' : '@';
    setComposerText(
      (current) => `${current}${current && !/\s$/.test(current) ? ' ' : ''}${trigger}`,
    );
    requestAnimationFrame(focusEditor);
  };
  const insertFiles = (files: readonly RepositoryFile[]) =>
    insertText(`${files.map((file) => `@${file.path}`).join(' ')} `);
  const workspace = useDesktopShellStore((state) => state.workspace);
  const executionMode = useDesktopShellStore((state) => state.executionMode);
  const cloudRepositoryBusy = useDesktopShellStore(
    (state) => shownConversationState(state).cloudRepositoryBusy,
  );
  const branchError = useDesktopShellStore((state) => state.branchError);
  const agentDiscoveryError = useDesktopShellStore((state) => state.agentDiscoveryError);
  const discoveringAgents = useDesktopShellStore((state) => state.discoveringAgents);
  const agentId = useDesktopShellStore((state) => state.agentId);
  const agents = useDesktopShellStore((state) => state.agents);
  const localHandoffWorking = useDesktopShellStore((state) => state.localHandoffWorking);
  const queuedFollowUps = useDesktopShellStore((state) => state.queuedFollowUps);
  const pausedFollowUpQueues = useDesktopShellStore((state) => state.pausedFollowUpQueues);
  const setPausedFollowUpQueues = useDesktopShellStore((state) => state.setPausedFollowUpQueues);

  const predictionsEnabled = useDesktopShellStore((state) => state.appSettings.messagePredictions);
  // Only the conversation's own composer, idle, with nothing queued after the reply.
  const prediction = useMessagePrediction(
    predictionsEnabled && groupComposer === null && editingMessageId === null && !busy
      ? conversationId
      : null,
    messages.at(-1)?.id ?? null,
  );

  const activeQueuedFollowUps = queuedFollowUps[conversationId] ?? [];
  const followUpQueuePause = pausedFollowUpQueues[conversationId];
  const composerPlaceholder =
    groupComposer !== null
      ? (groupComposer.placeholder ?? 'Message everyone, or use @name…')
      : workspace === null
        ? 'Pick a repository to begin'
        : cloudRepositoryBusy
          ? 'Preparing the cloud repository…'
          : !preview && !agentReady && !cloudAgentSelected
            ? agent === null && !discoveringAgents
              ? 'No agent available'
              : 'Starting the agent…'
            : startScreen
              ? 'Describe what you want done…'
              : // The boards' (D5): a reply, and / for the commands.
                'Reply… or / for commands';
  // The folded pill (D58): "Ask about this… ⌘L", and what runs.
  const compactComposerPlaceholder = 'Ask about this…';
  const floatingTitle = conversationTitle?.trim() || 'this conversation';
  const floatingChoices = navigation.threads
    .filter((thread) => thread.id !== conversationId)
    .slice(0, 6)
    .map((thread) => ({ id: thread.id, title: thread.title }));

  const composerInput = (
    <PromptInput
      className={`${floating ? 'desktop-composer-floating' : ''} desktop-composer relative ${startScreen && !floating ? 'z-10' : ''} mx-auto max-w-conversation [&_[data-slot=input-group]]:rounded-composer [&_[data-slot=input-group]]:border-border [&_[data-slot=input-group]]:bg-card`}
      data-compact-placeholder={
        floating && composerText.length === 0 ? compactComposerPlaceholder : undefined
      }
      style={
        floating
          ? ({
              '--desktop-compact-composer-width': `clamp(168px, calc(${compactComposerPlaceholder.length}ch + ${busy ? 140 : 64}px), 360px)`,
            } as CSSProperties)
          : undefined
      }
      multiple
      overlay={
        <div className={floating ? 'desktop-floating-input-extras' : 'contents'}>
          {pickerScope !== null && (
            <ComposerMenuPanel
              bodyClassName="flex max-h-none min-h-0 flex-col p-0"
              // The composer's own width (left-0 right-0 of its form): above it, edge to edge.
              className="flex flex-col"
              label={pickerScope === 'mention' ? 'Mention' : 'Skills'}
              placement={editingMessageId === null ? 'above' : 'below'}
              role="dialog"
            >
              <ComposerPicker
                commands={commandQuery !== null ? COMPOSER_COMMANDS : undefined}
                files={pickerScope === 'mention' ? repositoryFiles : []}
                initialFilter={mentionFilter}
                key={pickerScope}
                modelOptions={groupComposer === null ? modelConfigOptions : []}
                onChangeModel={(configId, value) => void changeModelConfig(configId, value)}
                onDismiss={() => {
                  clearPickerQuery();
                  requestAnimationFrame(focusEditor);
                }}
                onFocusHost={focusEditor}
                onMention={insertMention}
                onParticipant={(participant) =>
                  composerRef.current?.insertReference(
                    { kind: 'agent', id: participant.id, label: participant.name },
                    mentionQuery?.length ?? 0,
                  )
                }
                onPicked={(item: PickerItem) => {
                  // What inserts replaces the query itself; a command, a model or a setting
                  // leaves the draft as it was before / was typed.
                  if (
                    item.type === 'command' ||
                    item.type === 'model-choice' ||
                    item.type === 'setting' ||
                    item.type === 'action'
                  )
                    clearPickerQuery();
                  requestAnimationFrame(focusEditor);
                }}
                onReferences={({ files, targets }) => {
                  // Files are written as the agents read them (`@path`); the rest are references.
                  if (files.length > 0)
                    setComposerText((current) =>
                      replaceMentionQuery(
                        current,
                        `${files.map((file) => `@${file.path}`).join(' ')} `,
                      ),
                    );
                  targets.forEach((target, index) => {
                    if (index === 0 && files.length === 0)
                      composerRef.current?.insertReference(target, mentionQuery?.length ?? 0);
                    else insertArtifact(target);
                  });
                }}
                onSkill={insertSkill}
                onUnscope={() => {
                  clearPickerQuery();
                  requestAnimationFrame(() => plusMenuRef.current?.open());
                }}
                participants={participants}
                query={pickerQuery}
                ref={pickerRef}
                scope={pickerScope}
                skills={availableSkills}
                // The conversation itself is no reference of its own.
                targets={mentionTargets.filter(
                  (target) => target.kind !== 'chat' || target.id !== conversationId,
                )}
              />
            </ComposerMenuPanel>
          )}
        </div>
      }
      onSubmitCapture={(event) => {
        blockComposerSubmit(event, submissionBlocked);
      }}
      onSubmit={
        submissionBlocked
          ? () => {}
          : liveCall !== null && voiceCallActive && liveCall.call.assistant === conversationId
            ? // In a call, what is typed to the assistant is answered aloud too.
              (message) => {
                if (message.files.length > 0 || !message.text.trim()) return onSubmit(message);
                liveCall.call.type(message.text);
                setComposerText((current) => (current === message.text ? '' : current));
              }
            : groupComposer === null
              ? onSubmit
              : async (message) => {
                  await groupComposer.send(message);
                  setComposerText((current) => (current === message.text ? '' : current));
                }
      }
    >
      {groupComposer?.replyControl}
      {floating && composerText.length === 0 && <FloatingPillHint running={busy} />}
      <PromptInputBody className={floating ? 'desktop-floating-input-body' : undefined}>
        <div className={floating ? 'desktop-floating-input-extras' : 'contents'}>
          <ComposerContextChips />
          <PromptInputAttachments>
            {(attachment) => <PromptInputAttachment data={attachment} />}
          </PromptInputAttachments>
        </div>
        <ComposerReferenceEditor
          onChange={setComposerText}
          onKeyDown={(event) => {
            if (blockComposerSubmit(event, submissionBlocked)) return;
            if (pickerScope !== null && pickerRef.current?.handleKeyDown(event)) return;
            onKeyDown(event);
          }}
          placeholder={composerPlaceholder}
          ref={composerRef}
          {...(prediction === null || activeQueuedFollowUps.length > 0
            ? {}
            : { suggestion: prediction })}
          value={composerText}
        />
      </PromptInputBody>
      {branchError !== null && (
        <p className="type-caption px-3 pb-2 text-destructive">{branchError}</p>
      )}
      <div ref={setStatusContainer} />
      <ComposerToolbar
        // A new conversation's chips are the toolbar's row (D1).
        leading={startScreen && !floating ? workspaceControls : undefined}
        target={
          floating && groupComposer === null ? (
            <FloatingTargetChip
              choices={floatingChoices}
              offerNew={!startScreen}
              onChoose={(target) => {
                // The fullscreen view goes along: the composer floats over it, writing there.
                const cancel = carryShownTabTo(shellStore, target);
                if (target !== null) {
                  navigation.openConversation(target);
                  return;
                }
                // A new conversation is shown at once: nothing else may take the view later.
                navigation.newTask();
                cancel();
              }}
              onOpenConversation={() => restorePanel(shellStore)}
              title={floatingTitle}
            />
          ) : undefined
        }
        pluginScope={conversationId}
        liveDisabled={previewConversation || submissionBlocked}
        environment={{
          label: cloudAgentSelected
            ? 'Cloud sandbox'
            : executionMode === 'worktree'
              ? 'Worktree'
              : 'This computer',
          onOpen: onOpenEnvironment,
        }}
        onOpenScope={openPickerScope}
        insertFiles={insertFiles}
        plusMenuRef={plusMenuRef}
        repositoryFiles={
          groupComposer === null && workspaceControls !== undefined ? { preview } : undefined
        }
        pluginActions={
          <>
            <ComposerDictation
              disabled={previewConversation || voiceCallActive}
              focusEditor={focusEditor}
              insertText={insertText}
              onBlocked={onPluginBlocked}
              scope={composerScope}
              statusContainer={statusContainer}
            />
            <PluginComposerActions
              context={{
                disabled: previewConversation,
                send: (text) => onSubmit({ text, files: [] }),
              }}
              conversation={{ id: conversationId, busy }}
            />
          </>
        }
        agent={agent}
        agentDiscoveryError={agentDiscoveryError}
        agentId={agentId}
        agentRuntime={cloudAgentSelected ? 'cloud' : 'local'}
        agents={agents}
        busy={groupComposer === null ? busy : groupComposer.pending}
        canSubmit={
          groupComposer === null
            ? canSubmit && !submissionBlocked
            : groupComposer.ready &&
              !groupComposer.pending &&
              !submissionBlocked &&
              composerText.trim().length > 0
        }
        canSteer={agentReady && !submissionBlocked}
        changeModelConfig={changeModelConfig}
        changePlanFirst={changeShownPlanFirst}
        mentionTargets={mentionTargets}
        cloudHandoffWorking={cloudHandoffWorking}
        planFirst={shownPlanFirst}
        composerText={composerText}
        insertArtifact={insertArtifact}
        insertMention={insertMention}
        insertSkill={insertSkill}
        localHandoffWorking={localHandoffWorking}
        modelConfigOptions={modelConfigOptions}
        selectAgent={selectAgent}
        // The start screen picks the agent in +, as a conversation does (no agent chip).
        showAgentPicker={showAgentPicker}
        skills={availableSkills}
        status={groupComposer === null ? status : 'ready'}
        usage={latestUsage(messages)}
        planUsage={preview ? PREVIEW_PLAN_USAGE : null}
        stop={() => {
          if (activeQueuedFollowUps.length > 0) {
            setPausedFollowUpQueues((current) => ({ ...current, [conversationId]: 'interrupted' }));
          }
          onStop();
        }}
      />
    </PromptInput>
  );
  const composer = (
    <ExpandableComposer enabled={floating}>
      <div className={floating ? 'desktop-floating-input-extras' : 'contents'}>
        {groupComposer === null && (
          <QueuedFollowUps offline={!online && !floating}>
            <QueuedFollowUpList
              busy={busy}
              messages={activeQueuedFollowUps}
              onClear={onClearFollowUps}
              onDelete={onDeleteFollowUp}
              onEdit={(item) => {
                // Load the queued message back into this conversation's composer and
                // drop it from the queue, so the user can revise and resend it.
                setComposerText(item.message.text);
                onDeleteFollowUp(item.id);
                composerRef.current?.focus();
              }}
              onReorder={onReorderFollowUp}
              onResume={() =>
                setPausedFollowUpQueues((current) => ({ ...current, [conversationId]: undefined }))
              }
              onSendNow={onSteerFollowUp}
              paused={followUpQueuePause}
            />
          </QueuedFollowUps>
        )}
      </div>
      {composerInput}
    </ExpandableComposer>
  );

  const content = (
    <>
      {groupComposer === null &&
        cloudConversationStartupPhase !== null &&
        !cloudWorkspacePanelActive && (
          <CloudConversationStartup
            hasRepository={isCloudRepositoryWorkspace(workspace)}
            phase={cloudConversationStartupPhase}
          />
        )}
      {groupComposer === null && (
        <ToolApprovalPanel messages={messages} onAnswer={onToolApproval} preview={preview} />
      )}
      {!floating && startScreen ? (
        // The start screen's composer is near the top: its menus open down.
        <ComposerMenuSideProvider value="bottom">
          <NewConversationStart
            composer={composer}
            needsYou={needsYou}
            phone={phone}
            typed={composerText.trim().length > 0}
          />
        </ComposerMenuSideProvider>
      ) : floating || editingMessageContainer === null ? (
        composer
      ) : (
        createPortal(composer, editingMessageContainer)
      )}
      {!floating && !startScreen && <div className="mt-3 empty:hidden">{workspaceControls}</div>}
    </>
  );

  if (floating) return <div className="desktop-floating-composer shrink-0">{content}</div>;
  // The background spans the pane; the column inside moves aside for the pinned summary.
  return (
    <div
      className={`shrink-0 bg-background px-4 sm:px-6 ${
        editingMessageId !== null && editingMessageContainer !== null ? 'hidden' : ''
      } ${
        startScreen
          ? phone
            ? 'flex flex-1 flex-col overflow-y-auto pt-3 pb-5'
            : 'flex flex-1 flex-col justify-center overflow-y-auto py-5 pb-[12vh]'
          : 'pt-6 pb-5'
      }`}
    >
      <div
        className="mx-auto w-full max-w-conversation translate-x-(--thread-content-shift,0px) transition-[translate] duration-500 ease-out motion-reduce:transition-none"
        data-pip-obstacle
      >
        {content}
      </div>
    </div>
  );
}
